import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase, serviceSupabase } from '@/integrations/supabase/client';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from 'date-fns';
import { Input } from "@/components/ui/input";
import { toast } from 'sonner';

interface Stats {
  newUsers: number;
  newAssessments: number;
  assessmentsConducted: number;
}

interface TimeOption {
  id: string;
  label: string;
}

// Updated interface to include organization name and assessment count
interface UserDetail {
  id: string;
  first_name: string | null;
  last_name: string | null;
  organization_id: string | null;
  organization_name: string | null;
  organization_assessment_count: number;
  created_at: string;
}

// New Assessment detail interface
interface AssessmentDetail {
  id: string;
  title: string;
  candidates_invited: number;
  candidates_completed: number;
  organization_name: string | null;
  user_full_name: string;
  created_at: string;
}

// Type for the joined data from Supabase
interface ProfileWithOrg {
  id: string;
  first_name: string | null;
  last_name: string | null;
  organization_id: string | null;
  created_at: string;
  organizations: {
    id: string;
    name: string;
  } | null;
}

// Add a new interface for completed assessment details
interface CompletedAssessmentDetail {
  id: string;
  candidate_name: string;
  assessment_title: string;
  organization_name: string | null;
  score: number | null;
  completed_date: string;
}

// Change the interface name from InProgressAssessmentDetail to AssessmentManagementDetail
interface AssessmentManagementDetail {
  id: string;
  candidate_name: string;
  assessment_title: string;
  organization_name: string | null;
  candidate_id: string;
  assessment_id: string;
  conversation_id: string | null;
  status: string;
}

const timeOptions: TimeOption[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_week', label: 'Last 7 Days' },
  { id: 'last_month', label: 'Last 30 Days' }
];

const Reports: React.FC = () => {
  const [timeRange, setTimeRange] = useState('last_month');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    newUsers: 0,
    newAssessments: 0,
    assessmentsConducted: 0
  });
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetail[]>([]);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  
  // New assessment details state
  const [showAssessmentDetails, setShowAssessmentDetails] = useState(false);
  const [assessmentDetails, setAssessmentDetails] = useState<AssessmentDetail[]>([]);
  const [loadingAssessmentDetails, setLoadingAssessmentDetails] = useState(false);

  // Add state for completed assessments
  const [showCompletedDetails, setShowCompletedDetails] = useState(false);
  const [completedDetails, setCompletedDetails] = useState<CompletedAssessmentDetail[]>([]);
  const [loadingCompletedDetails, setLoadingCompletedDetails] = useState(false);

  // Rename the state variables
  const [showAssessmentManagement, setShowAssessmentManagement] = useState(false);
  const [assessmentManagementDetails, setAssessmentManagementDetails] = useState<AssessmentManagementDetail[]>([]);
  const [loadingAssessmentManagement, setLoadingAssessmentManagement] = useState(false);
  
  // Keep these the same
  const [updatingConversationId, setUpdatingConversationId] = useState<string | null>(null);
  const [conversationIdInputs, setConversationIdInputs] = useState<Record<string, string>>({});
  const [updatingCandidate, setUpdatingCandidate] = useState<string | null>(null);

  const getDateRange = (range: string): { start: Date, end: Date } => {
    const end = new Date();
    const start = new Date();

    switch (range) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case 'last_week':
        start.setDate(start.getDate() - 7);
        break;
      case 'last_month':
      default:
        start.setMonth(start.getMonth() - 1);
        break;
    }

    return { start, end };
  };

  const fetchUserDetails = async (range: string) => {
    try {
      setLoadingUserDetails(true);
      const { start, end } = getDateRange(range);

      // Log the date range for debugging
      console.log('Fetching profiles with date range:', { start: start.toISOString(), end: end.toISOString() });
      
      // Get profiles with their organizations using inner join
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id, 
          first_name, 
          last_name, 
          organization_id,
          created_at,
          organizations(id, name)
        `)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching profiles with orgs:', error);
        throw error;
      }

      console.log('Profiles with orgs fetched:', data);

      if (data && data.length > 0) {
        // Get all organization IDs
        const orgIds = data
          .map(profile => profile.organization_id)
          .filter(id => id !== null) as string[];
        
        // Fetch assessment counts for each organization
        const { data: assessmentData, error: assessmentError } = await supabase
          .from('assessments')
          .select('organization_id, id')
          .in('organization_id', orgIds.length > 0 ? orgIds : ['no-orgs']);

        if (assessmentError) {
          console.error('Error fetching organization assessments:', assessmentError);
          throw assessmentError;
        }

        console.log('Organization assessments fetched:', assessmentData);

        // Count assessments per organization
        const orgAssessmentCounts: Record<string, number> = {};
        
        if (assessmentData) {
          assessmentData.forEach(assessment => {
            if (assessment.organization_id) {
              orgAssessmentCounts[assessment.organization_id] = 
                (orgAssessmentCounts[assessment.organization_id] || 0) + 1;
            }
          });
        }
        
        console.log('Organization assessment counts:', orgAssessmentCounts);

        // Transform the data to include organization name and assessment count
        const transformedData = data.map(profile => {
          // Handle the fact that organizations might be an array in the result
          const organization = Array.isArray(profile.organizations) 
            ? profile.organizations[0] 
            : profile.organizations;
          
          // Get assessment count for this organization
          const orgId = profile.organization_id;
          const assessmentCount = orgId ? orgAssessmentCounts[orgId] || 0 : 0;
          
          return {
            id: profile.id,
            first_name: profile.first_name,
            last_name: profile.last_name,
            organization_id: profile.organization_id,
            organization_name: organization?.name || null,
            organization_assessment_count: assessmentCount,
            created_at: profile.created_at
          };
        });
        
        setUserDetails(transformedData);
      } else {
        setUserDetails([]);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      setUserDetails([]);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  // Update the fetchAssessmentDetails function to avoid problematic joins
  const fetchAssessmentDetails = async (range: string) => {
    try {
      setLoadingAssessmentDetails(true);
      const { start, end } = getDateRange(range);

      console.log('Fetching assessments with date range:', { start: start.toISOString(), end: end.toISOString() });
      
      // Simpler query that doesn't rely on joins
      const { data, error } = await supabase
        .from('assessments')
        .select('id, title, created_at, organization_id, user_id')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching assessments:', error);
        throw error;
      }

      console.log('Assessments data fetched:', data);

      if (data && data.length > 0) {
        // Get unique organization IDs and user IDs to fetch separately
        const orgIds = [...new Set(data.map(a => a.organization_id).filter(Boolean))];
        const userIds = [...new Set(data.map(a => a.user_id).filter(Boolean))];
        const assessmentIds = data.map(a => a.id);
        
        // Fetch candidates counts directly from the candidates table
        const { data: candidatesData, error: candidatesError } = await supabase
          .from('candidates')
          .select('assessment_id, status')
          .in('assessment_id', assessmentIds);
          
        if (candidatesError) {
          console.error('Error fetching candidates data:', candidatesError);
        }
        
        // Calculate invited and completed counts for each assessment
        const candidateCounts: Record<string, { invited: number, completed: number }> = {};
        
        if (candidatesData) {
          assessmentIds.forEach(id => {
            candidateCounts[id] = { invited: 0, completed: 0 };
          });
          
          candidatesData.forEach(candidate => {
            if (candidate.assessment_id) {
              // Count all candidates as invited
              candidateCounts[candidate.assessment_id].invited += 1;
              
              // Count only completed candidates
              if (candidate.status === 'completed') {
                candidateCounts[candidate.assessment_id].completed += 1;
              }
            }
          });
        }
        
        console.log('Calculated candidate counts:', candidateCounts);
        
        // Fetch organizations separately
        let organizationsMap: Record<string, string> = {};
        if (orgIds.length > 0) {
          const { data: orgsData, error: orgsError } = await supabase
            .from('organizations')
            .select('id, name')
            .in('id', orgIds);
            
          if (orgsError) {
            console.error('Error fetching organizations:', orgsError);
          } else if (orgsData) {
            organizationsMap = orgsData.reduce((acc, org) => {
              acc[org.id] = org.name;
              return acc;
            }, {} as Record<string, string>);
          }
        }
        
        // Fetch profiles separately
        let usersMap: Record<string, string> = {};
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', userIds);
            
          if (profilesError) {
            console.error('Error fetching profiles:', profilesError);
          } else if (profilesData) {
            usersMap = profilesData.reduce((acc, profile) => {
              const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
              acc[profile.id] = fullName || 'Unknown';
              return acc;
            }, {} as Record<string, string>);
          }
        }
        
        // Transform the data using the separately fetched info
        const transformedData = data.map(assessment => {
          const counts = candidateCounts[assessment.id] || { invited: 0, completed: 0 };
          
          return {
            id: assessment.id,
            title: assessment.title || 'Untitled Assessment',
            candidates_invited: counts.invited,
            candidates_completed: counts.completed,
            organization_name: assessment.organization_id ? 
              (organizationsMap[assessment.organization_id] || 'Unknown Organization') : 
              'No Organization',
            user_full_name: assessment.user_id ? 
              (usersMap[assessment.user_id] || 'Unknown User') : 
              'Unknown',
            created_at: assessment.created_at
          };
        });
        
        setAssessmentDetails(transformedData);
      } else {
        setAssessmentDetails([]);
      }
    } catch (error) {
      console.error('Error fetching assessment details:', error);
      setAssessmentDetails([]);
    } finally {
      setLoadingAssessmentDetails(false);
    }
  };

  // Update the fetchCompletedDetails function to improve date handling and debugging
  const fetchCompletedDetails = async (range: string) => {
    try {
      setLoadingCompletedDetails(true);
      const { start, end } = getDateRange(range);
      
      // Ensure end date is not before start date and add debug logs
      console.log('Fetching completed assessments for time range:', range);
      console.log('Start date:', start.toISOString());
      console.log('End date:', end.toISOString());
      
      // DEBUG: Temporarily remove date constraints to see if there's ANY data
      console.log('First checking if there are ANY completed candidates regardless of date:');
      const { data: allCandidates, error: allCandidatesError } = await supabase
        .from('candidates')
        .select('id, status')
        .eq('status', 'completed')
        .limit(5);
        
      if (allCandidatesError) {
        console.error('Error checking for any completed candidates:', allCandidatesError);
      } else {
        console.log('Sample of completed candidates (max 5):', allCandidates);
        console.log('Total completed candidates found (up to 5):', allCandidates?.length || 0);
      }
      
      // Then perform the actual date-constrained query
      console.log('Now fetching with date constraints:');
      
      // Query completed candidates with date range
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select('id, name, assessment_id, score, completed_date, status')
        .eq('status', 'completed')
        .gte('completed_date', start.toISOString())
        .lte('completed_date', end.toISOString())
        .order('completed_date', { ascending: false });

      if (candidatesError) {
        console.error('Error fetching completed candidates:', candidatesError);
        throw candidatesError;
      }

      console.log('Completed candidates filtered by date range:', candidatesData);
      console.log('Number of candidates found:', candidatesData?.length || 0);
      
      // Additional check if completed_date might be null
      console.log('Checking if there are completed candidates with null completed_date:');
      const { data: nullDateCandidates, error: nullDateError } = await supabase
        .from('candidates')
        .select('id, name, assessment_id, score, completed_date')
        .eq('status', 'completed')
        .is('completed_date', null)
        .limit(5);
        
      if (nullDateError) {
        console.error('Error checking null dates:', nullDateError);
      } else {
        console.log('Candidates with completed status but null date (up to 5):', nullDateCandidates);
        console.log('Count of completed candidates with null date (up to 5):', nullDateCandidates?.length || 0);
      }

      if (!candidatesData || candidatesData.length === 0) {
        setCompletedDetails([]);
        return;
      }

      // Get unique assessment IDs to fetch assessment details
      const assessmentIds = [...new Set(candidatesData.map(c => c.assessment_id).filter(Boolean))];
      
      // Fetch assessment data separately
      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('assessments')
        .select('id, title, organization_id')
        .in('id', assessmentIds);
        
      if (assessmentsError) {
        console.error('Error fetching assessments:', assessmentsError);
        throw assessmentsError;
      }
      
      console.log('Assessments data fetched:', assessmentsData);
      
      // Create a lookup map for assessments
      const assessmentsMap = (assessmentsData || []).reduce((acc, assessment) => {
        acc[assessment.id] = assessment;
        return acc;
      }, {} as Record<string, any>);
      
      // Get unique organization IDs
      const orgIds = [...new Set((assessmentsData || [])
        .map(a => a.organization_id)
        .filter(Boolean))];
      
      // Fetch organizations separately
      let organizationsMap: Record<string, string> = {};
      if (orgIds.length > 0) {
        const { data: orgsData, error: orgsError } = await supabase
          .from('organizations')
          .select('id, name')
          .in('id', orgIds);
          
        if (orgsError) {
          console.error('Error fetching organizations:', orgsError);
        } else if (orgsData) {
          organizationsMap = orgsData.reduce((acc, org) => {
            acc[org.id] = org.name;
            return acc;
          }, {} as Record<string, string>);
        }
      }
      
      // Transform the data to include all details
      const transformedData = candidatesData.map(candidate => {
        const assessment = assessmentsMap[candidate.assessment_id] || {};
        const orgId = assessment.organization_id;
        const orgName = orgId ? organizationsMap[orgId] : null;
        
        return {
          id: candidate.id,
          candidate_name: candidate.name || 'Unnamed Candidate',
          assessment_title: assessment.title || 'Unknown Assessment',
          organization_name: orgName || 'Unknown Organization',
          score: candidate.score,
          completed_date: candidate.completed_date
        };
      });
      
      setCompletedDetails(transformedData);
    } catch (error) {
      console.error('Error fetching completed assessment details:', error);
      setCompletedDetails([]);
    } finally {
      setLoadingCompletedDetails(false);
    }
  };

  // Update the function name and implementation
  const fetchAssessmentManagement = async (range: string) => {
    try {
      setLoadingAssessmentManagement(true);
      const { start, end } = getDateRange(range);
      
      console.log('Fetching all assessments for time range:', range);
      
      // Query all candidates within the time range without filtering by status
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select('id, name, assessment_id, conversation_id, status, created_at')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString())
        .order('created_at', { ascending: false });

      if (candidatesError) {
        console.error('Error fetching candidates:', candidatesError);
        throw candidatesError;
      }

      console.log('Candidates:', candidatesData);
      
      if (!candidatesData || candidatesData.length === 0) {
        setAssessmentManagementDetails([]);
        return;
      }

      // Get unique assessment IDs to fetch assessment details
      const assessmentIds = [...new Set(candidatesData.map(c => c.assessment_id).filter(Boolean))];
      
      // Fetch assessment data separately
      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('assessments')
        .select('id, title, organization_id')
        .in('id', assessmentIds);
        
      if (assessmentsError) {
        console.error('Error fetching assessments:', assessmentsError);
        throw assessmentsError;
      }
      
      // Create a lookup map for assessments
      const assessmentsMap = (assessmentsData || []).reduce((acc, assessment) => {
        acc[assessment.id] = assessment;
        return acc;
      }, {} as Record<string, any>);
      
      // Get unique organization IDs
      const orgIds = [...new Set((assessmentsData || [])
        .map(a => a.organization_id)
        .filter(Boolean))];
      
      // Fetch organizations separately
      let organizationsMap: Record<string, string> = {};
      if (orgIds.length > 0) {
        const { data: orgsData, error: orgsError } = await supabase
          .from('organizations')
          .select('id, name')
          .in('id', orgIds);
          
        if (orgsError) {
          console.error('Error fetching organizations:', orgsError);
        } else if (orgsData) {
          organizationsMap = orgsData.reduce((acc, org) => {
            acc[org.id] = org.name;
            return acc;
          }, {} as Record<string, string>);
        }
      }
      
      // Transform the data to include all details
      const transformedData = candidatesData.map(candidate => {
        const assessment = assessmentsMap[candidate.assessment_id] || {};
        const orgId = assessment.organization_id;
        const orgName = orgId ? organizationsMap[orgId] : null;
        
        // Initialize conversation ID inputs
        if (candidate.conversation_id) {
          setConversationIdInputs(prev => ({
            ...prev,
            [candidate.id]: candidate.conversation_id
          }));
        }
        
        return {
          id: candidate.id,
          candidate_name: candidate.name || 'Unnamed Candidate',
          assessment_title: assessment.title || 'Unknown Assessment',
          organization_name: orgName || 'Unknown Organization',
          candidate_id: candidate.id,
          assessment_id: candidate.assessment_id,
          conversation_id: candidate.conversation_id,
          status: candidate.status
        };
      });
      
      setAssessmentManagementDetails(transformedData);
    } catch (error) {
      console.error('Error fetching assessment management details:', error);
      setAssessmentManagementDetails([]);
    } finally {
      setLoadingAssessmentManagement(false);
    }
  };

  const handleConversationIdChange = (candidateId: string, value: string) => {
    setConversationIdInputs(prev => ({
      ...prev,
      [candidateId]: value
    }));
  };

  const handleUpdateConversationId = async (candidateId: string, assessmentId: string) => {
    try {
      setUpdatingConversationId(candidateId);
      const newConversationId = conversationIdInputs[candidateId]?.trim();
      
      if (!newConversationId) {
        toast.error('Please enter a valid conversation ID');
        return;
      }
      
      // Update the conversation ID in the database
      const { error: updateError } = await supabase
        .from('candidates')
        .update({ conversation_id: newConversationId })
        .eq('id', candidateId);

      if (updateError) {
        console.error('Error updating conversation ID:', updateError);
        toast.error('Failed to update conversation ID');
        return;
      }
      
      // Get updates by calling the recomplete-assessment function
      await handleGetUpdates(candidateId, assessmentId, newConversationId);
      
      // Update the assessment management details
      setAssessmentManagementDetails(prev => 
        prev.map(item => 
          item.id === candidateId 
            ? { ...item, conversation_id: newConversationId } 
            : item
        )
      );
      
      toast.success('Conversation ID updated successfully');
    } catch (error) {
      console.error('Error updating conversation ID:', error);
      toast.error('Failed to update conversation ID');
    } finally {
      setUpdatingConversationId(null);
    }
  };

  const handleGetUpdates = async (candidateId: string, assessmentId: string, conversationId: string) => {
    try {
      setUpdatingCandidate(candidateId);
      
      if (!conversationId) {
        toast.error('No conversation ID available');
        return;
      }

      const { data, error } = await serviceSupabase.functions.invoke('recomplete-assessment', {
        body: {
          conversationId,
          candidateId,
          assessmentId
        }
      });

      if (error) {
        console.error('Error getting updates:', error);
        toast.error('Failed to get updates');
        return;
      }

      toast.success('Assessment updated successfully');
      
      // Refresh the assessment management details
      fetchAssessmentManagement(timeRange);
    } catch (error) {
      console.error('Error getting updates:', error);
      toast.error('Failed to get updates');
    } finally {
      setUpdatingCandidate(null);
    }
  };

  const fetchStats = async (range: string) => {
    try {
      setLoading(true);
      const { start, end } = getDateRange(range);

      // Fetch new users (profiles created)
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('created_at')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (profilesError) throw profilesError;

      // Fetch new assessments
      const { data: assessments, error: assessmentsError } = await supabase
        .from('assessments')
        .select('created_at')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (assessmentsError) throw assessmentsError;

      // Fetch completed assessments (candidates who completed)
      const { data: candidates, error: candidatesError } = await supabase
        .from('candidates')
        .select('completed_date')
        .eq('status', 'completed')
        .gte('completed_date', start.toISOString())
        .lte('completed_date', end.toISOString());

      if (candidatesError) throw candidatesError;

      setStats({
        newUsers: profiles?.length || 0,
        newAssessments: assessments?.length || 0,
        assessmentsConducted: candidates?.length || 0
      });

      // If user details or assessment details are showing, update them too
      if (showUserDetails) {
        fetchUserDetails(range);
      }
      
      if (showAssessmentDetails) {
        fetchAssessmentDetails(range);
      }
      
      if (showCompletedDetails) {
        fetchCompletedDetails(range);
      }
      
      // If assessment management is showing, update them too
      if (showAssessmentManagement) {
        fetchAssessmentManagement(range);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserCardClick = async () => {
    setShowUserDetails(!showUserDetails);
    
    // Hide other details
    if (showAssessmentDetails) {
      setShowAssessmentDetails(false);
    }
    
    if (showCompletedDetails) {
      setShowCompletedDetails(false);
    }
    
    // Fetch user details if needed
    if (!showUserDetails && userDetails.length === 0) {
      fetchUserDetails(timeRange);
    }
  };
  
  // Update assessment card click handler
  const handleAssessmentCardClick = async () => {
    setShowAssessmentDetails(!showAssessmentDetails);
    
    // Hide other details
    if (showUserDetails) {
      setShowUserDetails(false);
    }
    
    if (showCompletedDetails) {
      setShowCompletedDetails(false);
    }
    
    // Fetch assessment details if needed
    if (!showAssessmentDetails && assessmentDetails.length === 0) {
      fetchAssessmentDetails(timeRange);
    }
  };

  // Add handler for Assessments Conducted card click
  const handleCompletedCardClick = async () => {
    setShowCompletedDetails(!showCompletedDetails);
    
    // Hide other details when showing completed assessments
    if (showUserDetails) {
      setShowUserDetails(false);
    }
    
    if (showAssessmentDetails) {
      setShowAssessmentDetails(false);
    }
    
    // Fetch completed details if not already loaded and we're showing the details
    if (!showCompletedDetails && completedDetails.length === 0) {
      fetchCompletedDetails(timeRange);
    }
  };

  // Update the handler name and implementation
  const handleAssessmentManagementClick = async () => {
    setShowAssessmentManagement(!showAssessmentManagement);
    
    // Hide other details
    if (showUserDetails) {
      setShowUserDetails(false);
    }
    
    if (showAssessmentDetails) {
      setShowAssessmentDetails(false);
    }
    
    if (showCompletedDetails) {
      setShowCompletedDetails(false);
    }
    
    // Fetch assessment management details if needed
    if (!showAssessmentManagement && assessmentManagementDetails.length === 0) {
      fetchAssessmentManagement(timeRange);
    }
  };

  useEffect(() => {
    fetchStats(timeRange);
  }, [timeRange]);

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-roundone-dark-purple mb-2">Reports</h1>
          <p className="text-muted-foreground mb-6">View system statistics and reports</p>
          
          <div className="inline-flex bg-muted rounded-full p-1 text-sm">
            {timeOptions.map((option, index) => (
              <Button
                key={option.id}
                variant="ghost"
                size="sm"
                className={cn(
                  "px-3 py-1 h-7 rounded-full transition-all",
                  timeRange === option.id 
                    ? "bg-white text-roundone-dark-purple shadow-sm" 
                    : "text-muted-foreground hover:text-roundone-dark-purple hover:bg-white/50",
                  "font-normal"
                )}
                onClick={() => setTimeRange(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
        
        <div className="grid gap-6">
          {loading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <Loader2 className="h-8 w-8 animate-spin text-roundone-purple" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card 
                  className={cn(
                    "cursor-pointer transition-all duration-200 hover:shadow-md",
                    showUserDetails && "ring-2 ring-roundone-purple"
                  )}
                  onClick={handleUserCardClick}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">New Users</CardTitle>
                    {showUserDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.newUsers}</div>
                    <p className="text-xs text-muted-foreground">
                      New users registered
                    </p>
                  </CardContent>
                </Card>
                <Card
                  className={cn(
                    "cursor-pointer transition-all duration-200 hover:shadow-md",
                    showAssessmentDetails && "ring-2 ring-roundone-purple"
                  )}
                  onClick={handleAssessmentCardClick}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">New Assessments</CardTitle>
                    {showAssessmentDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.newAssessments}</div>
                    <p className="text-xs text-muted-foreground">
                      Assessments created
                    </p>
                  </CardContent>
                </Card>
                <Card
                  className={cn(
                    "cursor-pointer transition-all duration-200 hover:shadow-md",
                    showCompletedDetails && "ring-2 ring-roundone-purple"
                  )}
                  onClick={handleCompletedCardClick}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Assessments Conducted</CardTitle>
                    {showCompletedDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.assessmentsConducted}</div>
                    <p className="text-xs text-muted-foreground">
                      Completed assessments
                    </p>
                  </CardContent>
                </Card>
                <Card
                  className={cn(
                    "cursor-pointer transition-all duration-200 hover:shadow-md",
                    showAssessmentManagement && "ring-2 ring-roundone-purple"
                  )}
                  onClick={handleAssessmentManagementClick}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Assessment Management</CardTitle>
                    {showAssessmentManagement ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {assessmentManagementDetails.length > 0 ? assessmentManagementDetails.length : '...'}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      All assessments
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* User details table */}
              {showUserDetails && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">User Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingUserDetails ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-roundone-purple" />
                      </div>
                    ) : userDetails.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No user data available for this time period</p>
                    ) : (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>First Name</TableHead>
                              <TableHead>Last Name</TableHead>
                              <TableHead>Organization</TableHead>
                              <TableHead className="text-right">Org Assessments</TableHead>
                              <TableHead>Created Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {userDetails.map((user) => (
                              <TableRow key={user.id}>
                                <TableCell>{user.first_name || 'N/A'}</TableCell>
                                <TableCell>{user.last_name || 'N/A'}</TableCell>
                                <TableCell>{user.organization_name || 'No Organization'}</TableCell>
                                <TableCell className="text-right">{user.organization_assessment_count}</TableCell>
                                <TableCell>
                                  {format(new Date(user.created_at), 'MMM d, yyyy HH:mm')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Assessment details table */}
              {showAssessmentDetails && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">Assessment Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingAssessmentDetails ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-roundone-purple" />
                      </div>
                    ) : assessmentDetails.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No assessment data available for this time period</p>
                    ) : (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Title</TableHead>
                              <TableHead className="text-right">Candidates Invited</TableHead>
                              <TableHead className="text-right">Candidates Completed</TableHead>
                              <TableHead>Organization</TableHead>
                              <TableHead>Created By</TableHead>
                              <TableHead>Created Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {assessmentDetails.map((assessment) => (
                              <TableRow key={assessment.id}>
                                <TableCell className="font-medium">{assessment.title}</TableCell>
                                <TableCell className="text-right">{assessment.candidates_invited}</TableCell>
                                <TableCell className="text-right">{assessment.candidates_completed}</TableCell>
                                <TableCell>{assessment.organization_name}</TableCell>
                                <TableCell>{assessment.user_full_name}</TableCell>
                                <TableCell>
                                  {format(new Date(assessment.created_at), 'MMM d, yyyy HH:mm')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Completed assessment details table */}
              {showCompletedDetails && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">Completed Assessment Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingCompletedDetails ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-roundone-purple" />
                      </div>
                    ) : completedDetails.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No completed assessments available for this time period</p>
                    ) : (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Candidate Name</TableHead>
                              <TableHead>Assessment Title</TableHead>
                              <TableHead>Organization</TableHead>
                              <TableHead className="text-right">Score</TableHead>
                              <TableHead>Completed Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {completedDetails.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.candidate_name}</TableCell>
                                <TableCell>{item.assessment_title}</TableCell>
                                <TableCell>{item.organization_name}</TableCell>
                                <TableCell className="text-right">{item.score !== null ? `${item.score}%` : 'N/A'}</TableCell>
                                <TableCell>
                                  {format(new Date(item.completed_date), 'MMM d, yyyy HH:mm')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Assessment Management table */}
              {showAssessmentManagement && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">Assessment Management</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingAssessmentManagement ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-roundone-purple" />
                      </div>
                    ) : assessmentManagementDetails.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No assessments available for this time period</p>
                    ) : (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Organization</TableHead>
                              <TableHead>Assessment Name</TableHead>
                              <TableHead>Candidate Name</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Conversation ID</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {assessmentManagementDetails.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.organization_name}</TableCell>
                                <TableCell>{item.assessment_title}</TableCell>
                                <TableCell>{item.candidate_name}</TableCell>
                                <TableCell>
                                  <span className={cn(
                                    "px-2 py-1 rounded-full text-xs font-medium",
                                    item.status === "invited" && "bg-blue-100 text-blue-800",
                                    item.status === "in_progress" && "bg-orange-100 text-orange-800",
                                    item.status === "completed" && "bg-green-100 text-green-800"
                                  )}>
                                    {item.status}
                                  </span>
                                </TableCell>
                                <TableCell className="flex items-center space-x-2">
                                  <Input 
                                    value={conversationIdInputs[item.id] || ''}
                                    onChange={(e) => handleConversationIdChange(item.id, e.target.value)}
                                    placeholder="Enter conversation ID"
                                    className="w-full"
                                  />
                                </TableCell>
                                <TableCell>
                                  <div className="flex space-x-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleUpdateConversationId(item.id, item.assessment_id)}
                                      disabled={updatingConversationId === item.id}
                                    >
                                      {updatingConversationId === item.id ? (
                                        <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Updating...
                                        </>
                                      ) : (
                                        'Update ID'
                                      )}
                                    </Button>
                                    {item.conversation_id && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleGetUpdates(item.id, item.assessment_id, item.conversation_id!)}
                                        disabled={updatingCandidate === item.id || !item.conversation_id}
                                      >
                                        {updatingCandidate === item.id ? (
                                          <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Updating...
                                          </>
                                        ) : (
                                          'Get Updates'
                                        )}
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Reports; 